/**
 * Theme system — provides dark/light mode with system preference detection.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useColorScheme as useSystemColorScheme } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ── Color Palettes ──

export const LIGHT_COLORS = {
  // ── Ocean Blue Palette ──
  primary: '#1565C0',
  primaryLight: '#E3F2FD',
  primaryDark: '#0D47A1',
  accent: '#42A5F5',
  accentLight: '#90CAF9',

  // ── Gold Accent ──
  gold: '#FFD54F',
  goldGlow: 'rgba(255, 213, 79, 0.12)',

  // ── Semantic ──
  secondary: '#34a853',
  success: '#34a853',
  warning: '#FF9500',
  error: '#C0392B',
  danger: '#C0392B',

  // ── Backgrounds ──
  background: '#F5F8FC',
  surface: '#FFFFFF',
  surfaceTinted: '#EBF1F8',
  card: '#FFFFFF',
  hover: '#DCE6F0',

  // ── Text ──
  text: '#1A202C',
  textPrimary: '#1A202C',
  textSecondary: '#4A5568',
  textDim: '#94A3B8',

  // ── Borders ──
  border: 'rgba(33, 150, 243, 0.12)',
  borderBold: 'rgba(33, 150, 243, 0.28)',

  // ── Chat ──
  userBubble: '#1565C0',
  chatBubbleUser: '#1565C0',
  chatBubbleBot: '#FFFFFF',
  chatBubbleBotText: '#1A202C',
  chatBubbleUserText: '#FFFFFF',

  // ── Tab Bar ──
  tabBar: '#FFFFFF',
  tabBarActive: '#1565C0',
  tabBarInactive: '#94A3B8',

  // ── UI ──
  inputBackground: '#EBF1F8',
  skeleton: 'rgba(33, 150, 243, 0.12)',
  overlay: 'rgba(0,0,0,0.5)',
  heroBackground: '#0D47A1',
  heroText: '#FFFFFF',
  heroSubtext: 'rgba(255,255,255,0.50)',
  chipBackground: '#EBF1F8',
  chipActiveBackground: '#E3F2FD',
  chipActiveBorder: '#1565C0',
  switchTrackOff: 'rgba(33, 150, 243, 0.12)',
  cardShadow: 'rgba(0,0,0,0.06)',
  statusBar: 'dark' as const,
} as const;

export const DARK_COLORS = {
  // ── Ocean Blue Palette ──
  primary: '#42A5F5',
  primaryLight: '#0E2137',
  primaryDark: '#1565C0',
  accent: '#90CAF9',
  accentLight: '#1565C0',

  // ── Gold Accent ──
  gold: '#FFD54F',
  goldGlow: 'rgba(255, 213, 79, 0.12)',

  // ── Semantic ──
  secondary: '#4CAF6A',
  success: '#4CAF6A',
  warning: '#FF9500',
  error: '#EF5350',
  danger: '#EF5350',

  // ── Backgrounds ──
  background: '#060E1A',
  surface: '#0A1929',
  surfaceTinted: '#0E2137',
  card: '#0A1929',
  hover: '#132D47',

  // ── Text ──
  text: '#F0F4F8',
  textPrimary: '#F0F4F8',
  textSecondary: '#8BA4C0',
  textDim: '#5A7B9A',

  // ── Borders ──
  border: 'rgba(33, 150, 243, 0.12)',
  borderBold: 'rgba(33, 150, 243, 0.28)',

  // ── Chat ──
  userBubble: '#1565C0',
  chatBubbleUser: '#1565C0',
  chatBubbleBot: '#0A1929',
  chatBubbleBotText: '#F0F4F8',
  chatBubbleUserText: '#FFFFFF',

  // ── Tab Bar ──
  tabBar: '#0A1929',
  tabBarActive: '#42A5F5',
  tabBarInactive: '#5A7B9A',

  // ── UI ──
  inputBackground: '#0E2137',
  skeleton: 'rgba(33, 150, 243, 0.12)',
  overlay: 'rgba(0,0,0,0.7)',
  heroBackground: '#0D47A1',
  heroText: '#FFFFFF',
  heroSubtext: 'rgba(255,255,255,0.50)',
  chipBackground: '#0E2137',
  chipActiveBackground: '#132D47',
  chipActiveBorder: '#42A5F5',
  switchTrackOff: 'rgba(33, 150, 243, 0.12)',
  cardShadow: '#000000',
  statusBar: 'light' as const,
} as const;

export type ThemeColors = {
  [K in keyof typeof LIGHT_COLORS]: string;
};
export type ColorSchemePreference = 'system' | 'light' | 'dark';

const THEME_STORAGE_KEY = 'fm-theme-preference';

// ── Theme Context ──

interface ThemeContextValue {
  colors: ThemeColors;
  isDark: boolean;
  colorScheme: ColorSchemePreference;
  setColorScheme: (scheme: ColorSchemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  colors: LIGHT_COLORS,
  isDark: false,
  colorScheme: 'light',
  setColorScheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const systemScheme = useSystemColorScheme();
  const [preference, setPreference] = useState<ColorSchemePreference>('light');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    AsyncStorage.getItem(THEME_STORAGE_KEY)
      .then((stored) => {
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
          setPreference(stored);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const setColorScheme = useCallback((scheme: ColorSchemePreference) => {
    setPreference(scheme);
    AsyncStorage.setItem(THEME_STORAGE_KEY, scheme).catch(() => {});
  }, []);

  const isDark = preference === 'system' ? systemScheme === 'dark' : preference === 'dark';
  const colors = isDark ? DARK_COLORS : LIGHT_COLORS;

  const value = useMemo(
    () => ({ colors, isDark, colorScheme: preference, setColorScheme }),
    [colors, isDark, preference, setColorScheme],
  );

  if (!loaded) return null;

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
